import React, { useState } from "react";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase/config";
import { useSite } from "../context/SiteContext";

function MailIcon({ className = "" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
      <path d="M4 6.5h16a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-9a1 1 0 0 1 1-1Z" stroke="currentColor" strokeWidth="1.7" />
      <path d="m4.5 7.2 7.2 5.2a1 1 0 0 0 1.2 0l6.6-5.2" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function PhoneIcon({ className = "" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
      <path d="M7.5 4.8c.4-.4 1-.4 1.4 0l2.1 2.1c.4.4.4 1 0 1.4l-1.2 1.2c-.3.3-.4.8-.2 1.2.9 1.8 2.4 3.3 4.2 4.2.4.2.9.1 1.2-.2l1.2-1.2c.4-.4 1-.4 1.4 0l2.1 2.1c.4.4.4 1 0 1.4l-1.1 1.1c-.9.9-2.2 1.2-3.4.8-4.2-1.5-7.8-5.1-9.3-9.3-.4-1.2-.1-2.5.8-3.4l1.1-1.1Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    </svg>
  );
}

const socialIcons = {
  facebook: <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3V2z" />,
  instagram: <><rect x="2" y="2" width="20" height="20" rx="5" ry="5" strokeWidth="1.6" /><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" strokeWidth="1.6" /><circle cx="17.5" cy="6.5" r="1" fill="currentColor" /></>,
  twitter: <path d="M4 4l5.5 7.5L4 20h2l4.5-5.5L14 20h6l-5.5-7.5L20 4h-2l-4.5 5.5L10 4H4z" strokeWidth="1.6" />,
  youtube: <><polygon points="9.75 15.02 15.5 11.75 9.75 8.48 9.75 15.02" strokeWidth="1.6" strokeLinejoin="round" /><rect x="2" y="6" width="20" height="12" rx="2" ry="2" strokeWidth="1.6" /></>,
  whatsapp: <><path d="M3 21l1.65-6.03A8.98 8.98 0 0 1 8.02 4.08a9 9 0 0 1 10.93 4.57 8.98 8.98 0 0 1-5.95 12.3c-1.12.3-2.29.29-3.4-.06L3 21z" strokeWidth="1.6" /><path d="M8.5 10.5c0 .5.5 1.5 1 2s2 2 3 2.5 1.5.5 2 .5c.5 0 1-.5 1.5-1s.5-1 1-1 .5 0 .5.5-.5 2-1 2.5-1.5 1-2.5 1-2-.5-3.5-1.5-2-2-2.5-3-.5-1.5-.5-2 .5-1 1-1 1 .5 1.5 1 .5 1 1 1 .5-.5 1-1 1-.5 1.5-.5" strokeWidth="1.6" /></>,
  reddit: <><circle cx="12" cy="12" r="10" strokeWidth="1.6" /><path d="M9.5 14.5s1 1 2.5 1 2.5-1 2.5-1" strokeWidth="1.6" strokeLinecap="round" /><circle cx="9" cy="11" r="1" fill="currentColor" /><circle cx="15" cy="11" r="1" fill="currentColor" /></>,
};

export default function Contact() {
  const { settings } = useSite();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleChange = (e) => setForm((s) => ({ ...s, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) return;
    setSending(true);
    try {
      await addDoc(collection(db, "contactSubmissions"), {
        ...form,
        createdAt: serverTimestamp(),
      });
      setSent(true);
      setForm({ name: "", email: "", subject: "", message: "" });
      setTimeout(() => setSent(false), 5000);
    } catch (err) {
      alert("Failed to send. Please try again.");
    }
    setSending(false);
  };

  return (
    <div className="bg-white text-black" style={{ fontFamily: "Montserrat, sans-serif" }}>
      {/* HERO */}
      <section className="bg-[#f7d8e8]">
        <div className="max-w-[1400px] mx-auto h-[165px] flex flex-col items-center justify-center text-center px-4">
          <h1 className="text-[28px] md:text-[32px] font-extrabold tracking-wide uppercase leading-none">Contact</h1>
          <div className="mt-2 flex items-center gap-3 text-[13px] md:text-[14px]">
            <span className="text-[#b8b8b8]">Home</span>
            <span className="text-[#b8b8b8]">/</span>
            <span className="text-black">Contact</span>
          </div>
        </div>
      </section>

      {/* CONTENT */}
      <section className="bg-white">
        <div className="max-w-[1240px] mx-auto px-6 md:px-10 lg:px-12 pt-[86px] pb-[94px]">
          <div className="grid lg:grid-cols-[1fr_300px] gap-20 items-start">
            {/* FORM */}
            <form onSubmit={handleSubmit} className="w-full">
              {sent && (
                <div className="mb-6 bg-green-50 border border-green-200 text-green-700 text-[13px] px-4 py-3 rounded">
                  Message sent successfully!
                </div>
              )}
              <div className="space-y-8">
                <input
                  name="name" value={form.name} onChange={handleChange}
                  placeholder="Name*" required
                  className="w-full h-[42px] border border-[#e1e1e1] px-4 text-[14px] outline-none placeholder:text-[#8f8f8f]"
                />
                <input
                  name="email" value={form.email} onChange={handleChange} type="email"
                  placeholder="E-mail*" required
                  className="w-full h-[42px] border border-[#e1e1e1] px-4 text-[14px] outline-none placeholder:text-[#8f8f8f]"
                />
                <input
                  name="subject" value={form.subject} onChange={handleChange}
                  placeholder="Subject"
                  className="w-full h-[42px] border border-[#e1e1e1] px-4 text-[14px] outline-none placeholder:text-[#8f8f8f]"
                />
                <textarea
                  name="message" value={form.message} onChange={handleChange}
                  placeholder="Message" rows="10"
                  className="w-full h-[260px] border border-[#e1e1e1] px-4 py-3 text-[14px] outline-none resize-none placeholder:text-[#8f8f8f]"
                />
              </div>
              <button type="submit" disabled={sending}
                className="mt-8 bg-black text-white uppercase font-bold tracking-wide px-8 h-[34px] text-[10px] md:text-[11px] disabled:opacity-50">
                {sending ? "Sending..." : "Send Message"}
              </button>
            </form>

            {/* RIGHT INFO */}
            <aside className="pt-[40px] lg:pt-[34px]">
              <div>
                <h3 className="text-[16px] font-extrabold uppercase tracking-wide">Address</h3>
                <p className="mt-2 text-[13px] text-[#9b9b9b]">{settings.address || "Bismarck, North Dakota"}</p>
              </div>

              <div className="mt-10">
                <h3 className="text-[16px] font-extrabold uppercase tracking-wide">Information</h3>
                <div className="mt-5 space-y-4 text-[13px] text-[#9b9b9b]">
                  <div className="flex items-center gap-3">
                    <MailIcon className="w-4 h-4 text-[#8d8d8d]" />
                    <span>{settings.email || "larsonk503@gmail.com"}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <PhoneIcon className="w-4 h-4 text-[#8d8d8d]" />
                    <span>{settings.phone || "12183041383"}</span>
                  </div>
                  <div className="flex items-center gap-3 pt-2">
                    {["facebook", "instagram", "twitter", "youtube", "whatsapp", "reddit"].map((key) => {
                      const url = settings.social?.[key];
                      if (!url) return null;
                      return (
                        <a key={key} href={url} target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center justify-center w-5 h-5 text-[#8d8d8d] hover:text-[#e84fa5] transition">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" className="w-full h-full">
                            {socialIcons[key]}
                          </svg>
                        </a>
                      );
                    })}
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* SUBSCRIBE */}
      <section className="bg-[#f4f4f4] border-t border-[#e8e8e8]">
        <div className="max-w-[1200px] mx-auto px-6 py-16">
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-5">
            <h3 className="text-[16px] md:text-[17px] font-extrabold uppercase tracking-wide">Get The Latest Updates</h3>
            <div className="flex w-full md:w-auto">
              <input type="email" placeholder="Enter your email" className="h-[34px] w-full md:w-[210px] border border-[#dedede] bg-white px-4 text-[13px] outline-none" />
              <button className="h-[34px] px-7 bg-black text-white text-[10px] font-bold uppercase">Subscribe</button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
